/**
 * Logger service for ESW Components
 * Provides consistent logging with formatting and prefix
 */

export enum LogLevel {
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LoggerConfig {
  prefix: string;
  minLevel: LogLevel;
  colors: boolean;
}

export class Logger {
  private static instance: Logger;
  private config: LoggerConfig = {
    prefix: 'ESW Components',
    minLevel: LogLevel.INFO,
    colors: true
  };

  private readonly colors: Record<string, string> = {
    reset: '\x1b[0m',
    prefix: '\x1b[36m', // Cyan
    info: '\x1b[32m', // Green
    warn: '\x1b[33m', // Yellow
    error: '\x1b[31m' // Red
  };

  private constructor() {}

  /**
   * Get logger instance (singleton)
   */
  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * Configure the logger
   */
  public configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Format a log message with prefix and optional coloring
   */
  private format(level: string, message: string): string {
    const { prefix, colors } = this.config;

    if (colors) {
      const colorCode = this.colors[level.toLowerCase()] ?? this.colors.reset;
      return `${this.colors.prefix}[${prefix}]${this.colors.reset} ${colorCode}${level}:${this.colors.reset} ${message}`;
    }

    return `[${prefix}] ${level}: ${message}`;
  }

  /**
   * Info level log
   */
  public info(message: unknown, ...optionalParams: unknown[]): void {
    if (this.config.minLevel <= LogLevel.INFO) {
      console.log(this.format('INFO', String(message)), ...optionalParams);
    }
  }

  /**
   * Warning level log
   */
  public warn(message: unknown, ...optionalParams: unknown[]): void {
    if (this.config.minLevel <= LogLevel.WARN) {
      console.warn(this.format('WARN', String(message)), ...optionalParams);
    }
  }

  /**
   * Error level log
   */
  public error(message: unknown, ...optionalParams: unknown[]): void {
    if (this.config.minLevel <= LogLevel.ERROR) {
      console.error(this.format('ERROR', String(message)), ...optionalParams);
    }
  }

  /**
   * Set the minimum log level
   */
  public setLevel(level: LogLevel): void {
    this.config.minLevel = level;
  }

  /**
   * Enable or disable colors
   */
  public setColors(enabled: boolean): void {
    this.config.colors = enabled;
  }
}

// Export a default logger instance for easy import
export const logger = Logger.getInstance();
